import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, FileText, Settings, Palette, Image as ImageIcon, 
  Users, Activity, LogOut, ChevronRight, Plus, Save, Trash2, 
  Search, Edit3, X, Bell, User,
  PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import { useCMS, CMSContext } from '../../context/CMSContext';

import Home from '../Home';
import About from '../About';
import Initiatives from '../Initiatives';
import Impact from '../Impact';
import Partnership from '../Partnership';
import Team from '../Team';
import News from '../News';

const Admin = () => {
  const cmsContext = useCMS();
  const { pages, settings, API_URL, refresh, assets } = cmsContext;
  const [activeTab, setActiveTab] = useState('pages');
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  // Form states
  const [pageForm, setPageForm] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [assetKey, setAssetKey] = useState('');

  // Handle auto sidebar collapse on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };
    handleResize(); // run on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (pages.length > 0 && selectedPageId === null) {
      setSelectedPageId(pages[0].id);
    }
  }, [pages, selectedPageId]);

  useEffect(() => {
    if (selectedPageId) {
      const page = pages.find(p => p.id === selectedPageId);
      if (page) setPageForm({...page});
    }
  }, [selectedPageId, pages]);

  useEffect(() => {
    setSettingsForm({...settings});
  }, [settings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      if (res.ok) {
        setIsAuthenticated(true);
        setError('');
      } else {
        setError('Invalid credentials. (Try admin/password)');
      }
    } catch (err) {
      setError('Error connecting to backend');
    }
  };

  const handleSavePage = async () => {
    try {
      const res = await fetch(`${API_URL}/pages/${pageForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pageForm)
      });
      
      // Save all sections as well
      let sectionsSaved = true;
      for (const section of pageForm.sections) {
        const secRes = await fetch(`${API_URL}/sections/${section.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(section)
        });
        if (!secRes.ok) sectionsSaved = false;
      }

      if (res.ok && sectionsSaved) {
        setMessage('Page and content updated successfully!');
        refresh();
        setTimeout(() => setMessage(''), 3000);
      } else if (res.ok) {
        setMessage('Page updated, but some sections failed to save.');
        refresh();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      alert("Error saving page or sections");
    }
  };

 const handleEditPage = (pageId) => {
  console.log("Edit clicked:", pageId);

  const page = pages.find(p => p.id === pageId);

  if (!page) {
    console.log("Page not found");
    return;
  }

  setPageForm({
    ...page,
    sections: page.sections || []
  });

  setSelectedPageId(pageId);
  setIsEditPanelOpen(true);
};

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsForm)
      });
      if (res.ok) {
        setMessage('Settings updated successfully');
        refresh();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      alert("Error saving settings");
    }
  };

  const handleSectionUpdate = (sectionId, field, value) => {
    const updatedSections = (pageForm.sections || []).map(s => {
      if (s.id === sectionId) {
        return { ...s, content: { ...s.content, [field]: value } };
      }
      return s;
    });
    setPageForm({ ...pageForm, sections: updatedSections });
  };

  const handleSaveSection = async (section) => {
    try {
      const res = await fetch(`${API_URL}/sections/${section.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(section)
      });
      if (res.ok) {
        setMessage(`${section.section_name} saved`);
        setTimeout(() => setMessage(''), 2000);
      }
    } catch (err) {
      alert("Error saving section");
    }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this page?")) return;
    try {
      const res = await fetch(`${API_URL}/pages/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setMessage('Page deleted');
        refresh();
        setSelectedPageId(pages[0]?.id || null);
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      alert("Error deleting page");
    }
  };

  const handleAddSection = async (pageId) => {
    const name = window.prompt("Section Name (e.g. Hero Section):");
    const type = window.prompt("Section Type (e.g. hero, about, features):");
    if (!name || !type) return;

    try {
      const res = await fetch(`${API_URL}/sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page_id: pageId, section_name: name, section_type: type, content: {} })
      });
      if (res.ok) {
        setMessage('Section added');
        refresh();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      alert("Error adding section");
    }
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm("Delete this section?")) return;
    try {
      const res = await fetch(`${API_URL}/sections/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setMessage('Section deleted');
        refresh();
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err) {
      alert("Error deleting section");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !assetKey) return alert("Select file and type key");
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('asset_key', assetKey);

    try {
      const res = await fetch(`${API_URL}/upload`, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        setMessage('Asset uploaded successfully');
        setUploadFile(null);
        setAssetKey('');
        refresh();
      }
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      alert("Error uploading asset");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="w-full max-w-md bg-white p-10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100">
          <div className="flex justify-center mb-8">
            <div className="bg-brandBlack p-3 rounded-xl">
               <div className="w-8 h-8 bg-brandGreen rounded-full"></div>
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-2 text-center">Welcome Back</h2>
          <p className="text-gray-400 text-center mb-8">Login to manage Eunikare CMS</p>
          
          {error && <div className="bg-red-50 text-red-500 p-3 rounded-lg mb-6 text-sm font-medium border border-red-100">{error}</div>}
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block mb-2 text-sm font-bold text-gray-700">Username</label>
              <input 
                type="text" 
                className="w-full border-gray-200 border p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brandGreen/20 transition-all" 
                placeholder="admin"
                value={username} onChange={(e)=>setUsername(e.target.value)}
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-bold text-gray-700">Password</label>
              <input 
                type="password" 
                className="w-full border-gray-200 border p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-brandGreen/20 transition-all" 
                placeholder="••••••••"
                value={password} onChange={(e)=>setPassword(e.target.value)}
              />
            </div>
            <button className="w-full bg-brandBlack text-white py-4 rounded-xl font-bold hover:bg-gray-800 transition-all shadow-lg">Login to Dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#F8F9FA] text-gray-800 overflow-hidden relative">
      
      {/* Sidebar mobile backdrop overlay */}
      {isSidebarOpen && (
        <div 
          onClick={() => setIsSidebarOpen(false)} 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        ></div>
      )}

      {/* SIDEBAR */}
      <aside className={`${isSidebarOpen ? 'w-72' : 'w-0 overflow-hidden'} fixed lg:relative z-50 lg:z-0 lg:shrink-0 h-full bg-[#0A0D10] text-white flex flex-col transition-all duration-300 ease-in-out`}>
        <div className="p-8 flex items-center gap-3 shrink-0">
           <div className="w-10 h-10 bg-brandGreen rounded-full flex items-center justify-center shrink-0">
              <div className="w-5 h-5 border-2 border-white rounded-full"></div>
           </div>
           <div className="leading-tight whitespace-nowrap">
             <h1 className="font-bold text-xl tracking-tight">EUNIKARE</h1>
             <p className="text-[10px] text-brandGreen font-bold tracking-[0.2em]">INTERNATIONAL</p>
           </div>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-1 overflow-hidden whitespace-nowrap">
          <p className="px-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-4">Content Management</p>
          <NavItem icon={<LayoutDashboard size={20}/>} label="Dashboard" active={activeTab==='dash'} onClick={()=>handleTabChange('dash')} />
          <NavItem icon={<FileText size={20}/>} label="Pages" active={activeTab==='pages'} onClick={()=>handleTabChange('pages')} />
          <NavItem icon={<ImageIcon size={20}/>} label="Media Library" active={activeTab==='media'} onClick={()=>handleTabChange('media')} />
          
          <p className="px-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-8 mb-4">Site Management</p>
          <NavItem icon={<Palette size={20}/>} label="Appearance" active={activeTab==='appearance'} onClick={()=>handleTabChange('appearance')} />
          <NavItem icon={<Settings size={20}/>} label="Site Settings" active={activeTab==='settings'} onClick={()=>handleTabChange('settings')} />
          
          <p className="px-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-8 mb-4">Administration</p>
          <NavItem icon={<Users size={20}/>} label="Users & Roles" active={activeTab==='users'} onClick={()=>handleTabChange('users')} />
          <NavItem icon={<Activity size={20}/>} label="Activity Logs" active={activeTab==='logs'} onClick={()=>handleTabChange('logs')} />
        </nav>

        <div className="p-6 border-t border-gray-800 shrink-0 whitespace-nowrap">
          <button onClick={()=>setIsAuthenticated(false)} className="flex items-center gap-3 text-red-400 hover:text-red-300 font-medium w-full px-4 py-2">
            <LogOut size={20}/>
            Logout
          </button>
        </div>
      </aside>

      {/* MAIN AREA */}
      <main className="flex-1 flex flex-col overflow-hidden w-full relative">
        
        {/* HEADER */}
        <header className="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-6 lg:px-10 shrink-0">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)} 
              className="p-2 text-gray-400 hover:text-brandBlack bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
            >
              {isSidebarOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
            </button>
            <div>
              <h2 className="text-xl font-bold capitalize">{activeTab}</h2>
              <p className="text-xs text-gray-400 font-medium">Dashboard &gt; {activeTab}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="relative">
              <Bell size={20} className="text-gray-400 cursor-pointer" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full border-2 border-white">3</span>
            </div>
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-2 rounded-full border border-gray-100">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                <User size={18} className="text-gray-500" />
              </div>
              <span className="text-sm font-bold">Admin</span>
              <ChevronRight size={14} className="text-gray-400 rotate-90" />
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-10">
          {message && <div className="fixed top-24 right-10 z-50 bg-brandGreen text-white px-6 py-3 rounded-xl shadow-2xl font-bold animate-in slide-in-from-right fade-in">{message}</div>}

          {activeTab === 'pages' && (
            <div className="flex flex-col lg:flex-row gap-8 h-full items-start">
              {/* Pages Sidebar */}
              <div className="w-full lg:w-80 h-[300px] lg:h-full bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col shrink-0">
                <div className="p-4 border-b border-gray-50 flex items-center gap-2">
                   <div className="relative flex-1">
                     <Search className="absolute left-3 top-2.5 text-gray-300" size={16} />
                     <input type="text" placeholder="Search pages..." className="w-full bg-gray-50 border-none rounded-lg pl-10 pr-4 py-2 text-sm focus:ring-1 focus:ring-brandGreen" />
                   </div>
                   <button 
                    onClick={async () => {
                      const title = window.prompt("Page Title:");
                      const slug = window.prompt("Page Slug (e.g. /new-page):");
                      if(title && slug) {
                        const res = await fetch(`${API_URL}/pages`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ title, slug })
                        });
                        if(res.ok) refresh();
                      }
                    }}
                    className="p-2 bg-brandGreen/10 text-brandGreen rounded-lg hover:bg-brandGreen hover:text-white transition-all"
                   >
                     <Plus size={18} />
                   </button>
                </div>
                <div className="flex-1 overflow-y-auto py-2">
                  {pages.map(p => (
                    <div key={p.id} className={`group/page relative ${selectedPageId === p.id ? 'bg-brandGreen/5 border-r-4 border-brandGreen' : ''}`}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => handleEditPage(p.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleEditPage(p.id); }}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-brandGreen/5 transition-colors text-left cursor-pointer"
                      >
                        <div>
                          <p className={`font-bold text-sm ${selectedPageId === p.id ? 'text-brandBlack' : 'text-gray-600'}`}>{p.title}</p>
                          <p className="text-[10px] text-gray-400 mt-1 font-medium">{p.slug}</p>
                        </div>
                        <ChevronRight size={14} className={selectedPageId === p.id ? 'text-brandGreen' : 'text-gray-300'} />
                      </div>
                      <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditPage(p.id); }}
                          className="px-3 py-2 text-xs font-bold text-gray-500 bg-gray-100 hover:text-brandGreen hover:bg-brandGreen/10 rounded-full transition-colors"
                          aria-label={`Edit ${p.title}`}
                          title={`Edit ${p.title}`}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDeletePage(p.id); }}
                          className="p-2 text-red-300 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          aria-label={`Delete ${p.title}`}
                          title={`Delete ${p.title}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-gray-50 bg-gray-50/50">
                  <button className="flex items-center justify-center gap-2 text-gray-400 hover:text-red-500 w-full font-bold text-xs py-2 transition-colors">
                    <Trash2 size={14} />
                    Trash (2)
                  </button>
                </div>
              </div>

              {/* Page Editor */}
              {pageForm ? (
                <div className="flex-1 h-full flex flex-col pb-6">
                  <div className="flex justify-between items-center mb-6 shrink-0">
                    <div>
                      <h3 className="text-2xl font-bold">{pageForm.title}</h3>
                      <p className="text-sm text-gray-400">Path: <span className="font-mono text-xs">{pageForm.slug}</span></p>
                    </div>
                    <div className="flex gap-3">
                      {!isEditPanelOpen ? (
                        <button onClick={() => setIsEditPanelOpen(true)} className="bg-brandGreen text-white px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-green-600 shadow-lg shadow-brandGreen/20 transition-all">
                          <Edit3 size={16} /> Edit Content
                        </button>
                      ) : (
                        <>
                          <button onClick={() => setIsEditPanelOpen(false)} className="bg-white border border-gray-200 px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-gray-50 shadow-sm text-gray-600">
                            <X size={16} /> Close Editor
                          </button>
                          <button onClick={handleSavePage} className="bg-brandBlack text-white px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-gray-800 shadow-lg shadow-black/20 transition-all">
                            <Save size={16} /> Save Changes
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col lg:flex-row overflow-hidden rounded-2xl border border-gray-200 shadow-lg relative bg-white min-h-[600px] lg:min-h-0">
                    {/* Left: Live Preview */}
                    <div className={`flex flex-col transition-all duration-500 ease-in-out border-b lg:border-b-0 lg:border-r border-gray-200 ${isEditPanelOpen ? 'w-full h-1/2 lg:h-full lg:w-[55%]' : 'w-full h-full'}`}>
                       <div className="bg-gray-100 border-b border-gray-200 flex items-center px-4 py-2 space-x-2 shrink-0 z-50">
                          <div className="w-3 h-3 rounded-full bg-red-400"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                          <div className="w-3 h-3 rounded-full bg-green-400"></div>
                          <div className="ml-4 flex-1 flex justify-center">
                            <div className="bg-white rounded px-3 py-1 text-[11px] text-gray-500 font-mono w-full max-w-sm flex items-center gap-2 border border-gray-200 shadow-sm">
                              <span className="text-gray-300">https://eunikare.org</span>{pageForm.slug}
                            </div>
                          </div>
                       </div>
                       <div className="flex-1 w-full overflow-y-auto bg-brandWhite relative" style={{ isolation: 'isolate' }}>
                         {/* We override the CMSContext for this specific component to feed it the draft pageForm state */}
                         <CMSContext.Provider value={{
                            ...cmsContext,
                            getPageData: (slug) => {
                              if (slug === pageForm.slug) {
                                const sectionsMapped = {};
                                pageForm.sections.forEach(s => { sectionsMapped[s.section_type] = s.content; });
                                return { ...pageForm, sectionData: sectionsMapped };
                              }
                              return cmsContext.getPageData(slug);
                            }
                         }}>
                           {(() => {
                              switch(pageForm.slug) {
                                case '/': return <Home />;
                                case '/about': return <About />;
                                case '/initiatives': return <Initiatives />;
                                case '/impact': return <Impact />;
                                case '/partnership': return <Partnership />;
                                case '/team': return <Team />;
                                case '/news': return <News />;
                                default: return <div className="p-20 text-center text-gray-400 font-medium">Preview not available for this path.</div>;
                              }
                           })()}
                         </CMSContext.Provider>
                       </div>
                    </div>

                    {/* Right: Edit Form Panel */}
                    <div className={`bg-gray-50 flex flex-col transition-all duration-500 ease-in-out overflow-hidden ${isEditPanelOpen ? 'w-full h-1/2 lg:h-full lg:w-[45%] opacity-100' : 'w-0 h-0 opacity-0'}`}>
                      <div className="flex-1 overflow-y-auto p-6 space-y-8">
                        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                    <h4 className="font-bold text-gray-700 mb-6">Page Settings</h4>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Page Title</label>
                        <input 
                          type="text" 
                          value={pageForm.title} 
                          onChange={(e)=>setPageForm({...pageForm, title: e.target.value})}
                          className="w-full border-gray-200 border p-3 rounded-xl focus:ring-1 focus:ring-brandGreen focus:outline-none" 
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Status</label>
                        <select 
                           value={pageForm.status} 
                           onChange={(e)=>setPageForm({...pageForm, status: e.target.value})}
                           className="w-full border-gray-200 border p-3 rounded-xl focus:ring-1 focus:ring-brandGreen focus:outline-none appearance-none"
                        >
                          <option value="Published">Published</option>
                          <option value="Draft">Draft</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Meta Description</label>
                        <textarea 
                           rows={3}
                           value={pageForm.meta_description}
                           onChange={(e)=>setPageForm({...pageForm, meta_description: e.target.value})}
                           className="w-full border-gray-200 border p-3 rounded-xl focus:ring-1 focus:ring-brandGreen focus:outline-none"
                        />
                        <p className="text-[10px] text-gray-300 mt-2 font-medium">{pageForm.meta_description?.length || 0} / 160 characters</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-700 px-2">Page Content</h4>
                    {(pageForm.sections || []).map(section => (
                      <div key={section.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden group">
                        <div className="p-6 flex items-center justify-between border-b border-gray-50">
                           <div className="flex items-center gap-4">
                             <div className="grid grid-cols-2 gap-1 opacity-20">
                               {[1,2,3,4,5,6].map(i=><div key={i} className="w-1 h-1 bg-gray-900 rounded-full"></div>)}
                             </div>
                             <span className="font-bold text-sm">{section.section_name}</span>
                           </div>
                           <div className="flex gap-2">
                             <button onClick={()=>handleDeleteSection(section.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={16}/></button>
                           </div>
                        </div>
                        <div className="p-8 bg-gray-50/30">
                           <div className="grid grid-cols-12 gap-8">
                              <div className="col-span-7 space-y-6">
                                {Object.entries(section.content).length === 0 && (
                                  <div className="text-center py-4 bg-white rounded-xl border border-dashed border-gray-200">
                                    <p className="text-xs text-gray-400 mb-4 italic">No fields in this section yet.</p>
                                    <button 
                                      onClick={() => {
                                        const key = window.prompt("Field Key (e.g. title, body):");
                                        if(key) handleSectionUpdate(section.id, key, "New content...");
                                      }}
                                      className="text-brandGreen font-bold text-[10px] uppercase hover:underline"
                                    >+ Add Field</button>
                                  </div>
                                )}
                                {Object.entries(section.content).map(([key, value]) => (
                                  key !== 'image' && (
                                    <div key={key} className="relative group/field">
                                      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2 capitalize">{key.replace(/_/g, ' ')}</label>
                                      {typeof value === 'string' && value.length > 50 ? (
                                        <textarea 
                                          rows={3}
                                          className="w-full border-gray-200 border p-3 rounded-xl bg-white text-sm focus:ring-2 focus:ring-brandGreen focus:border-brandGreen transition-all outline-none"
                                          value={value}
                                          onChange={(e) => handleSectionUpdate(section.id, key, e.target.value)}
                                        />
                                      ) : (
                                        <input 
                                          type="text"
                                          className="w-full border-gray-200 border p-3 rounded-xl bg-white text-sm focus:ring-2 focus:ring-brandGreen focus:border-brandGreen transition-all outline-none"
                                          value={value}
                                          onChange={(e) => handleSectionUpdate(section.id, key, e.target.value)}
                                        />
                                      )}
                                      <button 
                                        onClick={() => {
                                          const newContent = { ...section.content };
                                          delete newContent[key];
                                          const updatedSections = (pageForm.sections || []).map(s => s.id === section.id ? { ...s, content: newContent } : s);
                                          setPageForm({ ...pageForm, sections: updatedSections });
                                        }}
                                        className="absolute right-[-30px] top-1/2 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover/field:opacity-100 transition-opacity"
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  )
                                ))}
                                <div className="pt-4 flex items-center gap-4">
                                  <button onClick={()=>handleSaveSection(section)} className="bg-brandBlack text-white px-6 py-2 rounded-lg text-xs font-bold flex items-center gap-2">
                                     <Save size={14}/> Save Section
                                  </button>
                                  <button 
                                    onClick={() => {
                                      const key = window.prompt("Field Key (e.g. title, body):");
                                      if(key) handleSectionUpdate(section.id, key, "");
                                    }}
                                    className="text-gray-400 hover:text-brandBlack text-[10px] font-bold uppercase"
                                  >+ Add Field</button>
                                </div>
                              </div>
                              <div className="col-span-5">
                                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Featured Image</label>
                                <div className="rounded-2xl overflow-hidden aspect-video bg-gray-100 border-2 border-dashed border-gray-200 relative group/img">
                                  {section.content.image ? (
                                    <img src={section.content.image} className="w-full h-full object-cover" alt="section"/>
                                  ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-300">
                                       <ImageIcon size={32} />
                                       <span className="text-[10px] font-bold mt-2">NO IMAGE</span>
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                     <button 
                                       onClick={() => {
                                          const url = window.prompt("Enter Image URL (or use Media Library):");
                                          if(url) handleSectionUpdate(section.id, 'image', url);
                                       }}
                                       className="bg-white p-2 rounded-lg text-brandBlack hover:scale-110 transition-transform"
                                     ><Edit3 size={16}/></button>
                                     <button 
                                        onClick={() => handleSectionUpdate(section.id, 'image', '')}
                                        className="bg-white p-2 rounded-lg text-red-500 hover:scale-110 transition-transform"
                                     ><Trash2 size={16}/></button>
                                  </div>
                                </div>
                              </div>
                           </div>
                        </div>
                      </div>
                    ))}
                      <button onClick={()=>handleAddSection(pageForm.id)} className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 font-bold text-sm hover:border-brandGreen hover:text-brandGreen hover:bg-brandGreen/5 transition-all flex items-center justify-center gap-2">
                        <Plus size={18} />
                        Add Section
                      </button>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center h-[500px]">
                  <div className="text-center animate-pulse">
                    <FileText size={48} className="mx-auto text-gray-200 mb-4" />
                    <p className="text-gray-400 font-medium">Select a page to start editing</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'appearance' && (
            <div className="max-w-4xl space-y-8">
              <div className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100">
                <h3 className="text-2xl font-bold mb-8">Visual Identity</h3>
                <div className="grid grid-cols-2 gap-12">
                   <div className="space-y-6">
                     <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Brand Colors</h4>
                     {[
                        { label: 'Brand Green', key: 'brand-green' },
                        { label: 'Brand Mauve', key: 'brand-mauve' },
                        { label: 'Brand Beige', key: 'brand-beige' },
                        { label: 'Brand Black', key: 'brand-black' },
                     ].map(item => (
                       <div key={item.key} className="flex items-center gap-6">
                         <div className="w-12 h-12 rounded-2xl shadow-inner border border-gray-100 shrink-0" style={{ backgroundColor: settingsForm[item.key] || '#000' }}></div>
                         <div className="flex-1">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{item.label}</label>
                            <input 
                              type="text" 
                              className="w-full border-none bg-gray-50 p-2 rounded-lg text-sm font-mono focus:ring-1 focus:ring-brandGreen" 
                              value={settingsForm[item.key] || ''} 
                              onChange={(e)=>setSettingsForm({...settingsForm, [item.key]: e.target.value})}
                            />
                         </div>
                         <input 
                            type="color" 
                            className="w-8 h-8 rounded-full border-none cursor-pointer"
                            value={settingsForm[item.key] || '#000000'}
                            onChange={(e)=>setSettingsForm({...settingsForm, [item.key]: e.target.value})}
                         />
                       </div>
                     ))}
                   </div>
                   <div className="space-y-6">
                     <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Typography</h4>
                     <div className="p-6 bg-gray-50 rounded-3xl border border-gray-100">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-3">Primary Font</label>
                        <select 
                          className="w-full bg-white border-gray-200 border p-3 rounded-xl focus:ring-1 focus:ring-brandGreen focus:outline-none font-bold"
                          value={settingsForm['font-primary'] || 'Outfit'}
                          onChange={(e) => setSettingsForm({...settingsForm, 'font-primary': e.target.value})}
                        >
                          <option value="Outfit">Outfit (Default)</option>
                          <option value="Inter">Inter</option>
                          <option value="Roboto">Roboto</option>
                          <option value="Playfair Display">Playfair Display</option>
                        </select>
                        <div className="mt-6 p-4 bg-white rounded-xl border border-gray-100">
                           <p className="text-xs text-gray-400 mb-2 font-bold uppercase tracking-widest">Preview</p>
                           <p style={{ fontFamily: settingsForm['font-primary'] }} className="text-xl font-bold">The quick brown fox jumps over the lazy dog.</p>
                        </div>
                     </div>
                   </div>
                </div>
                <div className="mt-12 flex justify-end">
                   <button onClick={handleSaveSettings} className="bg-brandBlack text-white px-10 py-4 rounded-2xl font-bold hover:bg-gray-800 shadow-xl transition-all flex items-center gap-2">
                     <Save size={20}/> Update Theme
                   </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'media' && (
            <div className="space-y-8">
               <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm">
                  <h3 className="text-xl font-bold mb-6">Media Library</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                    {/* Add Media Button */}
                    <button className="aspect-square border-2 border-dashed border-gray-200 rounded-[2rem] flex flex-col items-center justify-center text-gray-300 hover:border-brandGreen hover:text-brandGreen hover:bg-brandGreen/5 transition-all group">
                       <Plus size={32} className="group-hover:scale-110 transition-transform"/>
                       <span className="text-[10px] font-bold mt-2 uppercase tracking-widest">Upload New</span>
                    </button>
                    {/* Sample Assets */}
                    {Object.entries(assets).map(([key, url]) => (
                      <div key={key} className="aspect-square bg-gray-100 rounded-[2rem] overflow-hidden group relative shadow-sm border border-gray-100">
                         <img src={url} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={key}/>
                         <div className="absolute inset-0 bg-brandBlack/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-4 text-center">
                            <p className="text-white text-[10px] font-bold truncate w-full mb-2">{key}</p>
                            <div className="flex gap-2">
                               <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"><Edit3 size={14}/></button>
                               <button className="p-2 bg-red-500/80 hover:bg-red-500 rounded-lg text-white"><Trash2 size={14}/></button>
                            </div>
                         </div>
                      </div>
                    ))}
                  </div>
               </div>

               <div className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm max-w-xl">
                 <h3 className="text-lg font-bold mb-6">Quick Upload</h3>
                 <form onSubmit={handleUpload} className="space-y-6">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Asset Key</label>
                      <input required type="text" value={assetKey} onChange={e=>setAssetKey(e.target.value)} className="w-full border-gray-200 border p-3 rounded-xl focus:ring-1 focus:ring-brandGreen" placeholder="e.g. hero_banner" />
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">Select Image</label>
                      <div className="relative">
                        <input required type="file" onChange={e=>setUploadFile(e.target.files[0])} className="opacity-0 absolute inset-0 cursor-pointer z-10" />
                        <div className="w-full border-gray-200 border-2 border-dashed p-10 rounded-xl flex flex-col items-center justify-center text-gray-400 group-hover:bg-gray-50 transition-colors">
                           <ImageIcon size={32} />
                           <span className="text-xs font-bold mt-2">{uploadFile ? uploadFile.name : 'Drop file here or click to browse'}</span>
                        </div>
                      </div>
                    </div>
                    <button type="submit" className="w-full bg-brandBlack text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2">
                       <Plus size={20}/> Upload Asset
                    </button>
                 </form>
               </div>
            </div>
          )}

          {['dash', 'users', 'logs'].includes(activeTab) && (
            <div className="flex flex-col items-center justify-center h-[500px] text-gray-300">
               <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                  <Activity size={40} />
               </div>
               <h3 className="text-xl font-bold text-gray-400">Coming Soon</h3>
               <p className="text-sm font-medium">This module is under development.</p>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

const NavItem = ({ icon, label, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all duration-200 group ${active ? 'bg-brandGreen text-white shadow-lg shadow-brandGreen/20' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
  >
    <span className={`${active ? 'text-white' : 'text-gray-500 group-hover:text-brandGreen'} transition-colors`}>
      {icon}
    </span>
    <span className="text-sm font-bold tracking-tight">{label}</span>
    {active && <ChevronRight size={14} className="ml-auto opacity-50" />}
  </button>
);

export default Admin;
